import BottomNavBar from '@/components/modules/navigation/BottomNavBar/BottomNavBar';
import FooterNavigation from '@/components/modules/navigation/FooterNavigation';
import FooterNavigationContainer from '@/components/modules/navigation/FooterNavigationContainer';
import NavBar from '@/components/modules/navigation/navbar/NavBar/NavBar';
import { SCREEN_NAME } from '@/constants/analytics';
import { MEGA_MENU_NAVIGATION_FEATURE_FLAG_PLACE_ONLY } from '@/constants/experimentConstants';
import Footer from '@/features/footer/Footer';
import MegaMenu from '@/features/mega-menu/MegaMenu';
import { useMegaMenuContext } from '@/features/mega-menu/MegaMenu.hooks';
import { HideOnScrollWrapper } from '@/features/search-header/SearchHeader';
import { classnames } from '@/helpers';
import { getScreenNameFromPath } from '@/helpers/analyticsTS';
import { useAppSelector } from '@/hooks';
import useMobileView from '@/hooks/useMobileView';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';
import ContainerLayout from '../ContainerLayout';
import { getFooterDisplayOption } from '../helpers';
import { PageViewLayout as PageViewLayoutProps } from '../types';

const PageViewLayout = ({
  children,
  footerDisplayOption,
  wrapperClass,
  headerNavigationContainerClassName,
  type,
  hideBottomNavBar = false,
  ...navbarProps
}: PageViewLayoutProps) => {
  const { isMobileView } = useMobileView();
  const flags = useAppSelector((state) => state.flags);
  const megaMenuContext = useMegaMenuContext();
  const location = useLocation();
  const isWhitelabel = useAppSelector((state) => state?.settings.isWhitelabel);
  const isPlacePage = getScreenNameFromPath(location.pathname) === SCREEN_NAME.COMPANY_DETAILS;

  let isMegaMenuFeature = false;

  if (!navbarProps.temporaryForceOldHeader) {
    if (isPlacePage) {
      isMegaMenuFeature = flags[MEGA_MENU_NAVIGATION_FEATURE_FLAG_PLACE_ONLY]?.value === 'on';
    } else {
      isMegaMenuFeature = true;
    }
  }

  const showBottomNavBar = type === 'mainView' && !hideBottomNavBar && isMobileView;

  return (
    <div className={wrapperClass}>
      <NavBar
        navClass={classnames(headerNavigationContainerClassName)}
        {...navbarProps}
        type={type}
        bottomSlot={(() => {
          if (navbarProps.bottomSlot !== undefined) {
            return navbarProps.bottomSlot;
          }
          if (isMegaMenuFeature && !isWhitelabel) {
            const megaMenu = (
              <MegaMenu
                {...megaMenuContext}
                renderNavigationContainer={(props) => {
                  const ref = props.navigationContainerRef.current;
                  return ref ? (
                    createPortal(<MegaMenu.Categories {...props} />, ref)
                  ) : (
                    /**
                     * need to render the navigation before the ref is mounted to make sure navigation appears on initial render
                     */
                    <nav id={props.navigationContainerId} className="mega-menu-navigation-container">
                      <MegaMenu.Categories {...props} />
                    </nav>
                  );
                }}
              />
            );

            if (megaMenuContext.activeTab) {
              return megaMenu;
            }
            /** if ref is not mounted use 56px for now for for Mega menu tabs (update if component changes) */
            const height = megaMenuContext.navRef.current?.getBoundingClientRect()?.height ?? 56;
            return <HideOnScrollWrapper height={height}>{megaMenu}</HideOnScrollWrapper>;
          }
          return undefined;
        })()}
        floatingSlot={
          navbarProps.floatingSlot ?? (isMegaMenuFeature && !isWhitelabel) ? (
            <Fragment>{megaMenuContext.renderNavigationContainer(megaMenuContext)}</Fragment>
          ) : null
        }
      />
      {children}
      {type === 'mainView' && (
        <Fragment>
          {isMegaMenuFeature && (
            <ContainerLayout
              className={classnames(
                footerDisplayOption?.from && footerDisplayOption.to && getFooterDisplayOption(footerDisplayOption),
              )}>
              <Footer />
            </ContainerLayout>
          )}
          {!isMegaMenuFeature && (
            <FooterNavigationContainer>
              <FooterNavigation />
            </FooterNavigationContainer>
          )}
        </Fragment>
      )}

      {showBottomNavBar && !megaMenuContext.activeTab && <BottomNavBar source={navbarProps.source} />}
    </div>
  );
};

export default PageViewLayout;
